import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Clapperboard, Disc3, SlidersHorizontal, Video, X } from "lucide-react";
import { fileUrl } from "@/lib/api";

const ICONS = {
  "audio-eng": SlidersHorizontal,
  videography: Video,
  "custom-beat": Disc3,
};

const titleIcon = (service) => {
  if (ICONS[service.id]) return ICONS[service.id];
  const t = (service.title || "").toLowerCase();
  if (t.includes("photo")) return Clapperboard;
  if (t.includes("video") || t.includes("vlog") || t.includes("content")) return Video;
  if (t.includes("mix") || t.includes("master")) return SlidersHorizontal;
  return Disc3;
};

export function ServiceDetailModal({ service, destination, onClose }) {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!service) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [service, onClose]);

  if (!service) return null;
  const Icon = titleIcon(service);
  const faq = Array.isArray(service.faq) ? service.faq : [];

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="service-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm sm:p-8"
        data-testid="service-detail-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#141419] shadow-[0_0_80px_rgba(110,231,183,0.08)]"
        >
          <div className="relative h-56 shrink-0 overflow-hidden">
            {service.image ? (
              <img
                src={fileUrl(service.image)}
                alt={service.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-emerald-300/20 to-black" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <button
              type="button"
              onClick={onClose}
              data-testid="service-detail-close"
              className="absolute right-4 top-4 rounded-full border border-white/15 bg-black/60 p-2 text-zinc-300 backdrop-blur-md transition-colors hover:text-white"
            >
              <X size={16} />
            </button>
            <div className="absolute inset-x-0 bottom-0 p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-300/40 bg-black/50 text-emerald-200 backdrop-blur-md">
                  <Icon size={15} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                    {service.price_label}
                  </p>
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight text-white">
                    {service.title}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6 sm:px-8">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">
              {service.description}
            </p>

            {faq.length > 0 && (
              <div data-testid="service-detail-faq">
                <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                  Common questions
                </p>
                <ul className="mt-3 divide-y divide-white/10 rounded-2xl border border-white/10 bg-[#0B0B0E]">
                  {faq.map((item, i) => {
                    const open = openFaq === i;
                    return (
                      <li key={i} data-testid={`service-detail-faq-item-${i}`}>
                        <button
                          type="button"
                          onClick={() => setOpenFaq(open ? -1 : i)}
                          data-testid={`service-detail-faq-toggle-${i}`}
                          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-bold text-white transition-colors hover:text-emerald-200"
                        >
                          <span>{item.question}</span>
                          <ChevronDown
                            size={16}
                            className={`shrink-0 text-emerald-300 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              key="content"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <p className="whitespace-pre-wrap px-5 pb-4 text-sm leading-relaxed text-zinc-400">
                                {item.answer}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-3 border-t border-white/10 bg-[#0F0F13] px-6 py-5 sm:flex-row sm:items-center sm:justify-end sm:px-8">
            <button
              type="button"
              onClick={onClose}
              data-testid="service-detail-cancel"
              className="rounded-full border border-white/15 px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:text-emerald-200"
            >
              Keep Browsing
            </button>
            <Link
              to={destination?.to || "/contact"}
              onClick={onClose}
              data-testid="service-detail-continue"
              className="flex items-center justify-center gap-2 rounded-full bg-emerald-300 px-6 py-3 text-xs font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_30px_rgba(110,231,183,0.3)]"
            >
              {destination?.cta || "Continue"} <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
